import { UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { createDeleteClientFormSchema, type DeleteClientFormValues } from '../logics/schema';
import { DeleteClientDialogPresentation } from '../presentations';
import { useDeletePartnerAdminsClient } from './useDeletePartnerAdminsClient';

type PresentationProps = React.ComponentProps<typeof DeleteClientDialogPresentation>;

type IUseDeleteClientDialog = (args: {
  /**
   * ダイアログを閉じるコールバック（親 Container から受け取る）
   */
  onClose: () => void;
  /**
   * 削除対象の利用企業名（Zod スキーマの完全一致チェックに使用）
   */
  name: string;
  /**
   * 削除対象の利用企業 ID
   */
  clientId: string;
  /**
   * 削除成功後コールバック（一覧再フェッチ・未与信カウント再取得を呼び出す）
   */
  onDelete: () => void;
}) => {
  state: {
    /**
     * react-hook-form のメソッド群
     */
    methods: UseFormReturn<DeleteClientFormValues>;
    /**
     * 削除ボタンを活性化するかどうか
     *
     * - フォームが valid（全フィールドが Zod スキーマを通過）のとき true
     * - Presentation の isConfirmDisabled に反転して渡す
     */
    isSubmitEnabled: boolean;
  };
  actions: {
    /**
     * ダイアログを閉じるハンドラ
     */
    handleClose: PresentationProps['onClose'];
    /**
     * フォーム送信ハンドラ
     */
    handleSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * 利用企業削除ダイアログの状態管理とアクションを提供するコンポーネントフック
 *
 * - T07e（PEN-743）: discriminated union 廃止・共通フォーム化
 * - T08（PEN-699）: バリデーション実行・DELETE API 送信・Toast・一覧再フェッチ・未与信カウント再取得
 * - threeLetterCode は Cookie から内部取得する（親 Container からの prop 受け渡し不要）
 */
export const useDeleteClientDialog: IUseDeleteClientDialog = ({
  onClose,
  name,
  clientId,
  onDelete,
}) => {
  const { showToast } = useToast();

  // Feature 内部で Cookie から取得（親 Container に依存しない）
  const threeLetterCode = getThreeLetterCode() ?? '';

  const methods = useForm<DeleteClientFormValues>({
    defaultValues: {
      type: 'churned',
      reason: '',
      clientName: '',
    },
    resolver: zodResolver(createDeleteClientFormSchema(name)),
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const { isValid } = methods.formState;

  const { trigger } = useDeletePartnerAdminsClient(threeLetterCode, clientId, {
    onSuccess: () => {
      showToast({ message: `「${name}」を削除しました`, status: 'success' });
      // ModalDialog.handleConfirm が onConfirm 呼び出し後に自動で onClose() を呼ぶため、ここでは onClose を呼ばない
      onDelete();
    },
    onError: error => {
      const message =
        error.response?.data?.errors?.[0] ?? '削除に失敗しました。時間をおいて再度お試しください。';
      showToast({ message, status: 'danger' });
    },
  });

  /**
   * ダイアログを閉じる
   */
  const handleClose: PresentationProps['onClose'] = () => {
    onClose();
  };

  /**
   * フォームを送信する
   *
   * - Zod バリデーション通過後、DELETE API を呼び出す
   * - reason_detail は reason フォームフィールドの値を使用する
   */
  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formData => {
    trigger({
      reason: formData.type,
      reason_detail: formData.reason,
    });
  });

  return {
    state: {
      methods,
      isSubmitEnabled: isValid,
    },
    actions: {
      handleClose,
      handleSubmit,
    },
  };
};
